import type React from "react";

import { cn } from "@/lib/utils";
import { Checkbox } from "../checkbox";
import { Label } from "../label/Label";

export type Option = {
  value: string;
  label: string;
};

interface CheckboxGroupProps {
  title?: string;
  options: Option[];
  selectedOptions?: Option[];
  onChange?: (selectedOptions: Option[]) => void;
  className?: string;
  labelClassName?: string;
  listClassName?: string;
}

/**
 * CheckboxGroup Component
 *
 * Renders a group of checkboxes with an optional title.
 * Manages selection state via `selectedOptions` and `onChange`.
 * Supports customizable styles for container, labels, and list.
 */
const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  title,
  options,
  selectedOptions = [],
  onChange,
  className = "",
  labelClassName = "",
  listClassName = "",
}) => {
  const handleCheckboxChange = (option: Option, checked: boolean) => {
    if (!onChange) return;

    const newSelectedOptions = checked
      ? [...selectedOptions, option]
      : selectedOptions.filter((selected) => selected.value !== option.value);

    onChange(newSelectedOptions);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {title && (
        <div className="flex items-center">
          <div className="mr-2 h-6 w-1 bg-[var(--stroke-base-primary)]" />
          <h2 className="text-md font-[var(--font-semibold)]">{title}</h2>
        </div>
      )}

      <div className={`space-y-3 ${listClassName}`}>
        {options.map((option) => (
          <div
            key={option.value}
            className="flex items-center space-x-2 text-[var(--text-gray-normal)]"
          >
            <Checkbox
              id={option.value}
              checked={selectedOptions.some(
                (selected) => selected.value === option.value,
              )}
              onCheckedChange={(checked) =>
                handleCheckboxChange(option, checked === true)
              }
            />
            <Label
              htmlFor={option.value}
              className={cn("text-md cursor-pointer", labelClassName)}
            >
              {option.label}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
};

export { CheckboxGroup };
